import {
  SwApiStore,
  SwDerivedApiStore,
} from "@sourcewhale/shared/src/svelte/api-stores/sw-api-store";
import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
import { getTodos } from "@sourcewhale/shared/src/svelte/api/users.js";
import { derived, get, writable } from "svelte/store";
import { queryTodos } from "../api/todos";

export const toDoCounterArgsStore = writable(null);

const initStore = writable(false);

const aiToDoCounterArgsStore = derived(
  [userStore, initStore],
  ([$userStore, $initStore]) => {
    if (!$initStore) return null;
    if (!$userStore) return {};
    const { aliases = [] } = $userStore;
    return { userId: aliases.sort().join(",") };
  },
);

const getCacheKey = (key) => {
  const user = get(userStore);
  const args = get(toDoCounterArgsStore);
  const currentAliases = JSON.stringify((user?.aliases || []).sort());
  const aliases = JSON.stringify((args?.userId.split(",") || []).sort());
  let cacheKey = key;
  if (args?.campaignType) cacheKey += `-${args.campaignType}`;
  if (args?.userId && currentAliases !== aliases) cacheKey += `-${args.userId}`;
  return cacheKey;
};

const toDoCounterStore = SwDerivedApiStore({
  get: getTodos,
  onMessage: ({ message }) => {
    if (!["update-to-dos", "update-send-mail"].includes(message.kind)) return;
    toDoCounterStore.get();
  },
  argsStore: toDoCounterArgsStore,
  argsTransform: (args) => {
    if (!args) return null;
    const params = {};
    if (args.userId) params.userIds = args.userId.split(",");
    if (args.campaignType) params.campaignType = args.campaignType;
    return [params];
  },
  cacheKey: () => getCacheKey("userTodos"),
  debounceDelayMs: 100,
  resetBeforeFetch: false,
});

const bounceCounterStore = SwDerivedApiStore({
  get: queryTodos,
  onMessage: ({ message }) => {
    if (message.kind === "update-candidates") {
      if (message.changes.find(({ update }) => update.bounceStatus)) {
        setTimeout(() => bounceCounterStore.get(), 3000);
      }
    }
  },
  argsStore: toDoCounterArgsStore,
  argsTransform: (args) => {
    if (!args) return null;
    const body = {
      kind: "bounce",
      campaignType: args.campaignType,
      status: "active",
      search: { value: "" },
      start: 0,
      length: 1,
      draw: 0,
    };
    if (args.userId) body.userIds = args.userId.split(",");
    return [body];
  },
  cacheKey: () => getCacheKey("queryTodosActiveBounce"),
  debounceDelayMs: 100,
  resetBeforeFetch: false,
});

const opportunityCounterStore = SwDerivedApiStore({
  get: queryTodos,
  onMessage: ({ message }) => {
    if (message.kind === "update-candidates") {
      if (message.changes.find(({ update }) => update.opportunityStatus)) {
        setTimeout(() => opportunityCounterStore.get(), 3000);
      }
    }
  },
  argsStore: toDoCounterArgsStore,
  argsTransform: (args) => {
    if (!args) return null;
    const body = {
      kind: "opportunity",
      campaignType: args.campaignType,
      status: "active",
      search: { value: "" },
      start: 0,
      length: 1,
      draw: 0,
    };
    if (args.userId) body.userIds = args.userId.split(",");
    return [body];
  },
  cacheKey: () => getCacheKey("queryTodosActiveOpportunity"),
  debounceDelayMs: 100,
  resetBeforeFetch: false,
});

const aiToDoCounterStore = SwDerivedApiStore({
  get: queryTodos,
  onMessage: ({ message }) => {
    if (message.kind === "update-ai-to-dos") {
      setTimeout(() => aiToDoCounterStore.get(), 3000);
    }
  },
  argsStore: aiToDoCounterArgsStore, // Only the current user may see their AI to-dos
  argsTransform: (args) => {
    if (!args) return null;
    const body = {
      kind: "aiToDo",
      status: "active",
      search: { value: "" },
      start: 0,
      length: 100, // TODO, probably better to move these todos to getTodos
      draw: 0,
      getCount: true,
    };
    if (args.userId) body.userIds = args.userId.split(",");
    return [body];
  },
  cacheKey: () => getCacheKey("queryTodosActiveAiToDo"),
  debounceDelayMs: 100,
  resetBeforeFetch: false,
});

const manualToDoCounterStore = SwDerivedApiStore({
  get: queryTodos,
  onMessage: ({ message }) => {
    if (message.kind === "update-manual-to-dos") {
      setTimeout(() => manualToDoCounterStore.get(), 3000);
    }
  },
  argsStore: toDoCounterArgsStore,
  argsTransform: (args) => {
    if (!args) return null;
    const body = {
      kind: "manualToDo",
      campaignType: args.campaignType,
      status: "active",
      search: { value: "" },
      start: 0,
      length: 0,
      draw: 0,
    };
    if (args.userId) body.userIds = args.userId.split(",");
    return [body];
  },
  cacheKey: () => getCacheKey("queryTodosActiveManualToDo"),
  debounceDelayMs: 100,
  resetBeforeFetch: false,
});

export const combinedToDoCounterStore = derived(
  [
    userStore,
    toDoCounterArgsStore,
    toDoCounterStore,
    bounceCounterStore,
    opportunityCounterStore,
    aiToDoCounterStore,
    manualToDoCounterStore,
    toDoCounterStore.meta,
    bounceCounterStore.meta,
    opportunityCounterStore.meta,
    opportunityCounterStore.meta,
    aiToDoCounterStore.meta,
    manualToDoCounterStore.meta,
  ],
  (
    [
      $userStore,
      $toDoCounterArgsStore,
      $toDoCounterStore,
      $bounceCounterStore,
      $opportunityCounterStore,
      $aiToDoCounterStore,
      $manualToDoCounterStore,
      $toDoMeta,
      $bounceMeta,
      $opportunityMeta,
      $aiToDoMeta,
      $manualToDoMeta,
    ],
    set,
  ) => {
    const fetching =
      $toDoMeta.fetching ||
      $bounceMeta.fetching ||
      $opportunityMeta.fetching ||
      $aiToDoMeta.fetching ||
      $manualToDoMeta.fetching;
    const firstFetchDone =
      $toDoMeta.firstFetchDone &&
      $bounceMeta.firstFetchDone &&
      $opportunityMeta.firstFetchDone &&
      $aiToDoMeta.firstFetchDone &&
      $manualToDoMeta.firstFetchDone;
    if (!firstFetchDone) {
      set({ toDoCounts: {}, totalCount: 0, formattedTotalCount: "" });
      return;
    }

    if (fetching) {
      return;
    }

    const aiCount =
      $toDoCounterArgsStore.userId === $userStore?.aliases.sort().join(",")
        ? $aiToDoCounterStore.recordsTotal || 0
        : 0;

    const toDoCounts = {
      ...($toDoCounterStore.toDoCounts || {}),
      bounce: $bounceCounterStore.recordsTotal || 0,
      ai: ($opportunityCounterStore.recordsTotal || 0) + aiCount,
      opportunity: $opportunityCounterStore.recordsTotal || 0,
      aiToDo: aiCount,
      manualToDo: $manualToDoCounterStore.recordsTotal || 0,
    };

    const totalCount =
      ($toDoCounterStore.toDos || 0) +
      ($bounceCounterStore.recordsTotal || 0) +
      ($opportunityCounterStore.recordsTotal || 0) +
      aiCount +
      ($manualToDoCounterStore.recordsTotal || 0);

    let formattedTotalCount = "";
    if (totalCount > 0) {
      formattedTotalCount = totalCount > 99 ? "99+" : totalCount.toString();
    }

    set({ toDoCounts, totalCount, formattedTotalCount });
  },
);

export const initializeToDoCounterStores = () => {
  if (get(initStore)) return;
  initStore.set(true);
  toDoCounterArgsStore.set({
    userId: "",
    campaignType: null,
  });

  userStore.subscribe((user) => {
    toDoCounterArgsStore.update((x) => ({
      ...x,
      aliases: user?.aliases?.sort().join(","),
    }));
  });
};

export const oldOpportunityCounterStore = SwApiStore({
  get: () => queryTodos({ kind: "checkOldOpportunities" }),
});

export const oldAiToDosCounterStore = SwApiStore({
  get: () => queryTodos({ kind: "checkOldAiToDos" }),
});
